import { Injectable, inject } from '@angular/core';

import type {
  AlbumEditorAlbumEventsPort,
  AlbumEditorModelChangedEvent,
  AlbumEditorPhotoSyncEvent,
} from '@lite-workspace/album-editor/ports';
import {
  type PhotoBookModelChangedData,
  type PhotoBookPreviewReadyData,
  SocketEvent,
  SocketRoom,
  SocketService,
} from './socket.service';
import { PhotoUploadCoordinatorService } from '../features/projects/domain/application/photo-upload-coordinator.service';

/**
 * The editor's feed of server-side album changes — the same PHOTO_BOOK room the albums tab listens on.
 * `vignettesJobRunning` lives on the editor route's subclass (album-editor-host.providers): its service stays lazy.
 */
@Injectable({ providedIn: 'root' })
export class EditorAlbumEventsPort implements AlbumEditorAlbumEventsPort {
  private readonly socket = inject(SocketService);
  private readonly coordinator = inject(PhotoUploadCoordinatorService);

  watchModelChanged(projectId: string, onChanged: (event: AlbumEditorModelChangedEvent) => void): () => void {
    this.socket.connect();
    const subscription = this.socket.joinRoom(SocketRoom.PHOTO_BOOK, projectId).subscribe(event => {
      if (event.eventType !== SocketEvent.PHOTO_BOOK_MODEL_CHANGED) return;
      const { bookId, version, statusOnly } = event.data as PhotoBookModelChangedData;
      onChanged({ albumId: bookId, version, statusOnly });
    });

    return () => {
      subscription.unsubscribe();
      this.socket.leaveRoom(SocketRoom.PHOTO_BOOK, projectId);
    };
  }

  // Its own join, not a branch inside watchModelChanged: room subjects are reference-counted, so a second
  // join costs nothing and each watcher keeps a teardown it alone owns.
  watchPreviewReady(projectId: string, onReady: (albumId: string) => void): () => void {
    this.socket.connect();
    const subscription = this.socket.joinRoom(SocketRoom.PHOTO_BOOK, projectId).subscribe(event => {
      if (event.eventType !== SocketEvent.PHOTO_BOOK_PREVIEW_READY) return;
      const { bookId, variant } = event.data as PhotoBookPreviewReadyData;
      // A copy's previews (another print format) are not the album the editor shows.
      if (variant) return;
      onReady(bookId);
    });

    return () => {
      subscription.unsubscribe();
      this.socket.leaveRoom(SocketRoom.PHOTO_BOOK, projectId);
    };
  }

  watchCommentsChanged(projectId: string, onChanged: (photobookId: string) => void): () => void {
    this.socket.connect();
    const subscription = this.socket.joinRoom(SocketRoom.PHOTO_BOOK, projectId).subscribe(event => {
      if (event.eventType !== SocketEvent.PHOTO_BOOK_COMMENTS_CHANGED) return;
      onChanged((event.data as { photobookId: string }).photobookId);
    });

    return () => {
      subscription.unsubscribe();
      this.socket.leaveRoom(SocketRoom.PHOTO_BOOK, projectId);
    };
  }

  // Routed through the upload coordinator on purpose: it already owns the FILE room and its reconnect recovery.
  // Редактору отдаём только те моменты, которые описаны его портом: завершение проекта целиком и
  // принятый аплоад — сигналы мастера, и расширять ради них публичный тип библиотеки незачем.
  watchPhotoSync(projectId: string, onEvent: (event: AlbumEditorPhotoSyncEvent) => void): () => void {
    return this.coordinator.watchPhotoSync(projectId, event => {
      if (event.kind === 'project-completed' || event.kind === 'upload-accepted') return;
      onEvent(event);
    });
  }
}
